/**
 * Зоны страницы трендов (владелец 16.09): вместо «Главная | Галерея» — капсулы «Видео | Фото».
 *
 * Принадлежность НЕ привязана к типу медиа насильно: блок объявляет зону сам (config.zone —
 * video | photo | both), карточка отмечает зоны галочками (trend_zones), а подсказка по медиа
 * (ролик или тренд «фото → видео» → «Видео», иначе «Фото») — только умолчание, пока админ карточку
 * не тронул. Картинка на странице «Видео» — просто галочка.
 *
 * Маршруты: корень /bots/trends = зона «Видео», /bots/trends/photo — «Фото»,
 * /bots/trends/<zone>/<категория> — подраздел галереи зоны; лендинги карточек как раньше.
 */
import type { BackendCard } from './popularCardApply.ts'

export type TrendZone = 'video' | 'photo'

export interface TrendZoneDef {
	key: TrendZone
	label: string
	path: string
}

/** Порядок = порядок капсул; первая — корневая (/bots/trends). */
export const TREND_ZONES: TrendZoneDef[] = [
	{ key: 'video', label: 'Видео', path: '/bots/trends' },
	{ key: 'photo', label: 'Фото', path: '/bots/trends/photo' },
]
export const ROOT_ZONE: TrendZone = TREND_ZONES[0].key

export const isTrendZone = (v: unknown): v is TrendZone => v === 'video' || v === 'photo'

export const zoneLabel = (zone: TrendZone): string => TREND_ZONES.find((z) => z.key === zone)?.label ?? zone

/** Подсказка по медиа — умолчание для карточки без явной отметки. */
export function autoTrendZones(card: Pick<BackendCard, 'video_url' | 'pipeline'>): TrendZone[] {
	return card.video_url || card.pipeline?.enabled ? ['video'] : ['photo']
}

/** Зоны карточки: явная отметка админа главнее подсказки — включая пустую («ни в одной зоне»). */
export function cardTrendZones(card: Pick<BackendCard, 'video_url' | 'pipeline' | 'trend_zones'>): TrendZone[] {
	if (Array.isArray(card.trend_zones)) return card.trend_zones.filter(isTrendZone)
	return autoTrendZones(card)
}

/** Зона лендинга карточки, пришедшего БЕЗ зоны в адресе (ссылка с главной, из поиска, разосланная
 *  раньше — до появления зон): карточка живёт ровно в одной зоне → открываем её, чтобы фото-тренд не
 *  показывался на вкладке «Видео». В обеих зонах или ни в одной зоны не выбрать — корневая, как раньше. */
export function landingZoneOf(card: Pick<BackendCard, 'video_url' | 'pipeline' | 'trend_zones'>): TrendZone | null {
	const zones = cardTrendZones(card)
	return zones.length === 1 ? zones[0] : null
}

/** Зоны блока: config.zone; пусто — «Видео» (так вели себя все блоки до появления зон). */
export function sectionTrendZones(cfg: Record<string, unknown> | undefined): TrendZone[] {
	const z = cfg?.zone
	if (z === 'both') return ['video', 'photo']
	return z === 'photo' ? ['photo'] : ['video']
}

/** Секции, из которых собирается зона: ленты и галереи. */
export const ZONE_SECTION_TYPES = new Set(['trend_feed', 'trend_gallery'])

/** Адрес зоны, при категории — её подраздела (у корневой зоны категория живёт под явным слагом). */
export function zonePath(zone: TrendZone, category?: string | null): string {
	const def = TREND_ZONES.find((z) => z.key === zone) ?? TREND_ZONES[0]
	if (!category) return def.path
	return `${zone === ROOT_ZONE ? `/bots/trends/${zone}` : def.path}/${category}`
}

/** Контекст лендинга карточки (/bots/trends/<ctx>/tool/<slug>): корень зоны «Видео» — канонический
 *  путь без контекста, остальное — зона (и категория), чтобы после клика вкладка не перескакивала. */
export function zoneLandingCtx(zone: TrendZone, category?: string | null): string | null {
	if (category) return `${zone}/${category}`
	return zone === ROOT_ZONE ? null : zone
}

export interface TrendsPath {
	zone: TrendZone
	/** Зона названа в самом адресе (/photo, /video/<категория>), а не подставлена умолчанием.
	 *  Лендинг без названной зоны (ссылка с главной, из поиска, старая) сам выбирает зону по карточке. */
	explicitZone: boolean
	category: string | null
	/** Слаг лендинга, если /tool/<slug> пришёл хвостом wildcard-маршрута. */
	cardSlug: string | null
	/** Первый сегмент, не являющийся зоной: старый адрес галереи (all | popular | <категория>). */
	legacy: string | null
}

/** Разбор хвоста /bots/trends/*. */
export function parseTrendsPath(wildcard: string | null | undefined): TrendsPath {
	const segs = (wildcard || '').split('/').filter(Boolean)
	let cardSlug: string | null = null
	const toolAt = segs.indexOf('tool')
	if (toolAt >= 0) {
		cardSlug = segs[toolAt + 1] || null
		segs.splice(toolAt)
	}
	if (segs.length === 0) return { zone: ROOT_ZONE, explicitZone: false, category: null, cardSlug, legacy: null }
	const first = segs[0]
	if (isTrendZone(first)) return { zone: first, explicitZone: true, category: segs[1] || null, cardSlug, legacy: null }
	return { zone: ROOT_ZONE, explicitZone: false, category: null, cardSlug, legacy: first }
}
